import React from 'react';
import { ArrowRight, DollarSign, Leaf, AlertTriangle, TrendingUp, Wrench, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';
import RelatedArticles from '@/components/RelatedArticles';
import PageSEO from '@/components/PageSEO';
import { ReplacementPageLinks } from '@/components/InternalLinks';

const COST_CHART = [
  { age: '0–5 years', avgRepair: '$150–$500', replCost: '$5,000–$8,000', verdict: 'Repair', color: 'bg-green-100 text-green-700' },
  { age: '6–8 years', avgRepair: '$300–$800', replCost: '$5,000–$8,000', verdict: 'Repair (usually)', color: 'bg-green-50 text-green-600' },
  { age: '9–11 years', avgRepair: '$400–$1,200', replCost: '$5,500–$9,000', verdict: 'Case-by-case', color: 'bg-yellow-50 text-yellow-700' },
  { age: '12–15 years', avgRepair: '$500–$2,000', replCost: '$6,000–$10,000', verdict: 'Replace', color: 'bg-orange-50 text-orange-700' },
  { age: '15+ years', avgRepair: '$800+', replCost: '$6,000–$12,000', verdict: 'Definitely replace', color: 'bg-red-50 text-red-700' },
];

const RepairVsReplacePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PageSEO breadcrumbs={[
        { name: 'Home', url: '/' },
        { name: 'Repair vs. Replace AC', url: '/repair-vs-replace-ac' },
      ]} />
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="repair-vs-replace-heading"
          >
            Should You Repair or Replace Your Air Conditioner?
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            When your AC breaks down, the first question is always the same: fix it or replace it?
            This guide helps Louisiana homeowners make a smart, informed decision.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Get an Instant Replacement Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Repair Cost vs System Age Chart */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <DollarSign className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Repair Cost vs. System Age
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            Use this chart as a starting point. The older your system, the less it makes sense to invest in repairs.
          </p>

          {/* Table */}
          <div className="bg-white rounded-xl overflow-hidden border border-slate-200">
            <div className="grid grid-cols-4 bg-[#0F172A] text-white text-sm font-bold">
              <div className="p-4">System Age</div>
              <div className="p-4">Typical Repair</div>
              <div className="p-4">Replacement Cost</div>
              <div className="p-4">Recommendation</div>
            </div>
            {COST_CHART.map((row, i) => (
              <div key={i} className={`grid grid-cols-4 text-sm ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                <div className="p-4 text-[#0F172A] font-medium">{row.age}</div>
                <div className="p-4 text-slate-600">{row.avgRepair}</div>
                <div className="p-4 text-slate-600">{row.replCost}</div>
                <div className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.color}`}>
                    {row.verdict}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-sm mt-4 italic">
            * Costs are estimates based on the Greater New Orleans area. Actual pricing depends on system size, brand, and complexity.
          </p>
        </div>
      </section>

      {/* The 5,000 Rule */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            The $5,000 Rule
          </h2>
          <div className="bg-white rounded-2xl p-8 border border-slate-200">
            <p className="text-slate-600 text-lg mb-4">
              Many HVAC professionals use a simple formula to help homeowners decide:
            </p>
            <div className="bg-[#0F172A] text-white rounded-xl p-6 text-center mb-4">
              <p className="text-sm text-slate-400 mb-2">The Formula</p>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Age of Unit &times; Cost of Repair = Decision Number
              </p>
              <p className="text-slate-300 mt-2">If the result is over $5,000, replace the unit.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-xl">
                <p className="font-bold text-green-700 mb-1">Example: Repair</p>
                <p className="text-sm text-slate-600">
                  6-year-old unit &times; $400 repair = $2,400 → <strong>Repair makes sense</strong>
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded-xl">
                <p className="font-bold text-red-700 mb-1">Example: Replace</p>
                <p className="text-sm text-slate-600">
                  12-year-old unit &times; $600 repair = $7,200 → <strong>Replace is smarter</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Refrigerant Phaseout */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              R-22 Refrigerant Phaseout
            </h2>
          </div>
          <div className="space-y-4 text-slate-600 text-lg">
            <p>
              If your AC system uses R-22 (Freon), you're dealing with a refrigerant that's been
              phased out of production since January 2020. The remaining supply is limited and
              increasingly expensive — recharging an R-22 system can cost $150–$300+ per pound.
            </p>
            <p>
              Modern systems use R-410A or the newer R-454B, which are more efficient and environmentally
              friendly. If your system still runs on R-22, replacement is strongly recommended.
            </p>
          </div>
          <div className="mt-6 bg-[#FFF7ED] border border-orange-200 rounded-xl p-6">
            <p className="font-bold text-[#F97316] mb-2">How to check your refrigerant type:</p>
            <p className="text-slate-600">
              Look at the data plate on your outdoor unit. The refrigerant type is listed near the
              model number. If it says "R-22" or "HCFC-22," your unit uses the phased-out refrigerant.
            </p>
          </div>
        </div>
      </section>

      {/* Energy Efficiency */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <Leaf className="w-7 h-7 text-green-600" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Energy Efficiency: The Hidden Cost of Keeping an Old Unit
            </h2>
          </div>
          <div className="space-y-4 text-slate-600 text-lg mb-8">
            <p>
              Even if your old AC is still running, it's likely costing you more every month. Older units
              lose efficiency over time, and today's systems are significantly more efficient.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <TrendingUp className="w-8 h-8 text-[#0EA5E9] mx-auto mb-3" />
              <p className="text-2xl font-bold text-[#0F172A]">10–13</p>
              <p className="text-sm text-slate-500">SEER (pre-2015 units)</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center border-2 border-[#0EA5E9]">
              <TrendingUp className="w-8 h-8 text-[#F97316] mx-auto mb-3" />
              <p className="text-2xl font-bold text-[#0F172A]">15–22+</p>
              <p className="text-sm text-slate-500">SEER (modern units)</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <p className="text-2xl font-bold text-green-600">30–50%</p>
              <p className="text-sm text-slate-500">Potential savings</p>
            </div>
          </div>
          <p className="text-slate-500 text-sm mt-6">
            In Louisiana, where AC runs 8+ months per year, upgrading from a 10-SEER to a 16-SEER
            unit can save $500–$1,000+ annually on electricity costs.
          </p>
        </div>
      </section>

      {/* Signs to Replace */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <Wrench className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              5 Signs It's Time to Replace
            </h2>
          </div>
          <div className="space-y-3">
            {[
              { num: 1, text: 'Your unit is over 12 years old and needs a major repair ($500+).' },
              { num: 2, text: 'You\'ve had 2 or more breakdowns in the past 12 months.' },
              { num: 3, text: 'Your energy bills have steadily increased despite regular maintenance.' },
              { num: 4, text: 'Your system uses R-22 refrigerant (phased out in 2020).' },
              { num: 5, text: 'Some rooms in your home are consistently warmer or cooler than others.' },
            ].map((item) => (
              <div key={item.num} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <span className="w-8 h-8 bg-[#F97316] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                  {item.num}
                </span>
                <p className="text-[#0F172A]">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="prose prose-slate max-w-none">
            <p>
              If your system is aging, the first step is knowing exactly how old it is. Use our{' '}
              <a href="/check-ac-age" className="text-[#0EA5E9]">AC age checker</a>{' '}
              to decode your serial number. Want to understand how Louisiana's climate affects
              your system? Read about{' '}
              <a href="/ac-unit-lifespan-louisiana" className="text-[#0EA5E9]">AC lifespan in Louisiana</a>.
            </p>
            <p>
              Ready to explore your options? Get a free{' '}
              <a href="/instant-hvac-estimate-new-orleans" className="text-[#0EA5E9]">instant HVAC estimate</a>{' '}
              in minutes — no phone call required. You can also check{' '}
              <a href="/ac-replacement-cost-new-orleans" className="text-[#0EA5E9]">AC replacement costs in New Orleans</a>{' '}
              or browse{' '}
              <a href="/hvac-pricing-new-orleans" className="text-[#0EA5E9]">HVAC pricing</a>{' '}
              for your area.
            </p>
          </div>
        </div>
      </section>

      <RelatedArticles currentPath="/repair-vs-replace-ac" />

      <ReplacementPageLinks />

      {/* CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            See What a New System Would Cost
          </h2>
          <p className="text-slate-300 mb-8">
            Get an instant, no-obligation estimate for your HVAC replacement. Fast, free, online.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Start My Instant Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RepairVsReplacePage;
